import { getCountries, getStates, processCreditCardDeposit, getOpenPayClient } from '@/utils/api';
import Vue from 'vue';
import Vuex from 'vuex';
import { getField, updateField } from 'vuex-map-fields';
import i18n from '@/utils/i18n';

Vue.use(Vuex);

const state = {
  selectedCreditCard: {
    id: '',
    cardNumber: '',
    cardMonth: '',
    cardYear: '',
    cardCVV: '',
    cardCredentials: '',
    address: '',
    country: 'US',
    state: 'OH',
    zipCode: '',
    city: '',
    phoneNumber: '',
    bonusClaimed: false,
  },
  countries: [],
  states: [],
  creditCardDepositResponse: {},
  depositStatus: {
    loading: false,
    success: false,
    error: false,
  },
};

const getters = {
  getField,
  getBillingAddress: state => {
    return `
        ${state.selectedCreditCard.address}
        ${state.selectedCreditCard.country ? state.selectedCreditCard.country + ',' : ''}
        ${state.selectedCreditCard.state ? state.selectedCreditCard.state + ',' : ''}
        ${state.selectedCreditCard.city ? state.selectedCreditCard.city + ',' : ''}
        ${state.selectedCreditCard.zipCode}
        ${state.selectedCreditCard.phoneNumber}`;
  },
};

const mutations = {
  updateField,
  setSelectedCreditCard(state, cardData) {
    state.selectedCreditCard.id = cardData.id;
    state.selectedCreditCard.cardNumber = cardData.cardNumber;
    state.selectedCreditCard.cardMonth = cardData.expMonth;
    state.selectedCreditCard.cardYear = cardData.expYear;
    state.selectedCreditCard.cardCVV = '';
    state.selectedCreditCard.cardCredentials = cardData.nameOnCard;
    state.selectedCreditCard.address = cardData.billingStreet;
    state.selectedCreditCard.phone = cardData.phone;
    state.selectedCreditCard.country = cardData.billingCountry;
    state.selectedCreditCard.state = cardData.billingState;
    state.selectedCreditCard.zipCode = cardData.billingZipCode;
    state.selectedCreditCard.city = cardData.billingCity;
  },
  resetSelectedCreditCard(state, rootState) {
    state.selectedCreditCard.id = null;
    state.selectedCreditCard.cardNumber = '';
    state.selectedCreditCard.cardMonth = '';
    state.selectedCreditCard.cardYear = '';
    state.selectedCreditCard.cardCVV = '';
    state.selectedCreditCard.cardCredentials = '';
    state.selectedCreditCard.address = '';
    state.selectedCreditCard.phone = '';
    state.selectedCreditCard.country = 'US';
    state.selectedCreditCard.state = 'OH';
    state.selectedCreditCard.zipCode = '';
    state.selectedCreditCard.city = '';
  },
  setCountries(state, countries) {
    state.countries = countries;
  },
  setStates(state, states) {
    state.states = states;
  },
  setDepositStatus(state, row) {
    state.depositStatus = {
      ...state.depositStatus,
      ...row,
    };
  },
};

const actions = {
  trySetDefaultInformation({ commit, rootState }) {
    const player = rootState.player.player.PlayerInfo;
    const cardData = {
      id: '',
      cardNumber: '',
      expMonth: '',
      expYear: '',
      nameOnCard: '',
      billingStreet: '',
      billingCountry: '',
      billingState: '',
      billingZipCode: '',
      billingCity: '',
    };
    if (player) {
      cardData.nameOnCard = player.first_name + ' ' + player.last_name;
      cardData.phone = player.primary_phone;
      cardData.billingCity = player.address_city;
      cardData.billingZipCode = player.address_postal_code;
      cardData.billingState = player.address_state;
      cardData.billingCountry = player.address_country;
      cardData.billingStreet = player.address_street;
    }
    commit('setSelectedCreditCard', cardData);
  },
  tryGetOpenPayClient() {
    return getOpenPayClient();
  },
  fetchCountries({ rootState }) {
    return getCountries(rootState.settings.brandSettings.book_id, rootState.settings.selectedLocale.short_code);
  },
  tryGetCountries({ commit, rootState }) {
    getCountries(rootState.settings.selectedLocale.code).then(r => {
      if (r.data.IsSuccess) {
        commit('setCountries', r.data.Data);
      }
    });
  },
  tryGetStates({ commit }, iso2) {
    if (iso2)
      getStates(iso2).then(r => {
        if (r.data.IsSuccess) {
          commit('setStates', r.data.Data);
        }
      });
  },
  tryProcessCreditCardDeposit({ commit, dispatch }, formData) {
    Vue.prototype.$gtmProvider.sendDepositEvent(
      {
        eventLabel: 'visa_attempt',
        eventValue: Vue.prototype.$gaHelper.getDepositValue(formData.amount),
      },
      {
        label: 'visa_attempt',
        value: Vue.prototype.$gaHelper.getDepositValue(formData.amount),
      },
    );
    commit('setDepositStatus', { loading: true });
    commit('depositStore/setPostDepositLoading', true, { root: true });
    processCreditCardDeposit(formData)
      .then(r => {
        commit('depositStore/setPostDepositLoading', false, { root: true });

        if (r.data.IsSuccess && r.data.Data.success) {
          Vue.prototype.$gtmProvider.sendDepositEvent(
            {
              eventLabel: 'visa_success',
              eventValue: Vue.prototype.$gaHelper.getDepositValue(formData.amount),
            },
            {
              label: 'visa_success',
              value: Vue.prototype.$gaHelper.getDepositValue(formData.amount),
            },
          );
          if (r.data.Data.redirectUrl && !formData.userDocument) {
            window.location.replace(r.data.Data.redirectUrl);
            return;
          }
          dispatch('player/tryGetBalance', null, { root: true });
          commit('setDepositStatus', {
            success: formData.userDocument ? 'DepositView.CreditCard.SuccessMessage' : r.data.Data,
            error: false,
          });
        } else {
          Vue.prototype.$gtmProvider.sendDepositEvent(
            {
              eventLabel: 'visa_fail',
              eventValue: Vue.prototype.$gaHelper.getDepositValue(formData.amount),
            },
            {
              label: 'visa_fail',
              value: Vue.prototype.$gaHelper.getDepositValue(formData.amount),
            },
          );
          commit('setDepositStatus', {
            success: false,
            error: r.data.Data.displayMessage || i18n.t('Exceptions.' + r.data.Data.message),
          });
        }
        commit('setDepositStatus', { loading: false });
      })
      .catch(() => {
        Vue.prototype.$gtmProvider.sendDepositEvent(
          {
            eventLabel: 'visa_fail',
            eventValue: Vue.prototype.$gaHelper.getDepositValue(formData.amount),
          },
          {
            label: 'visa_fail',
            value: Vue.prototype.$gaHelper.getDepositValue(formData.amount),
          },
        );
      });
  },
  resetStatus({ commit }) {
    commit('setDepositStatus', { success: false, error: false });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
